import React, { useState } from 'react';
import { useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';

import {
  Card,
  CardContent,
  Select,
  MenuItem,
  Switch,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  FormControl,
  InputLabel,
  FormControlLabel,
  Typography,
  Box,
  useTheme,
  SelectChangeEvent,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';

import { DUPLICATED_EMAIL_ERROR, DUPLICATED_PHONE_ERROR } from '@bandido/shared/constants';

import { useGetAllAgencies } from '@frontend/features/agencies/queries';
import { client } from '@frontend/features/common/services';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';

interface UserAgencyProps {
  selectedAgency?: number | null;
  userId: string;
  clientId: string;
}

export const UserAgency: React.FC<UserAgencyProps> = ({ selectedAgency, userId, clientId }) => {
  const [isDisabled, setIsDisabled] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const { addToast } = useSnackBar();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const { data: agencies } = useGetAllAgencies({
    pageSize: 10000,
    currentPage: 1,
    search: '',
  });

  const handleAgencyChange = async (event: SelectChangeEvent<string>) => {
    try {
      await client.patch('/user/agency', {
        agencyId: Number(event.target.value),
        clientId,
        userId,
      });
      addToast({
        message: t('updateClientAgency.updateSuccess'),
        color: 'success',
      });
      queryClient.invalidateQueries();
    } catch (e: any) {
      if (e.response?.data?.error === DUPLICATED_EMAIL_ERROR) {
        addToast({
          message: t('clients.create.createdErrorEmail'),
          color: 'error',
        });
      } else if (e.response?.data?.error === DUPLICATED_PHONE_ERROR) {
        addToast({
          message: t('clients.create.createdErrorPhone'),
          color: 'error',
        });
      } else {
        addToast({
          message: t('clients.create.createdError'),
          color: 'error',
        });
      }
    }
  };

  const handleConfirmChange = () => {
    setIsDisabled(false);
    setOpenDialog(false);
  };

  const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setOpenDialog(true);
    } else {
      setIsDisabled(true);
    }
  };

  return (
    <Card
      sx={{
        boxShadow: 'none',
        borderRadius: theme.spacing(3),
      }}
      elevation={4}
    >
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="subtitle2">{t('updateClientAgency.title')}</Typography>

          <FormControlLabel
            control={<Switch checked={!isDisabled} onChange={handleSwitchChange} />}
            label={
              isDisabled ? (
                <LockIcon htmlColor={theme.palette.primary.main} />
              ) : (
                <LockOpenIcon htmlColor={theme.palette.primary.main} />
              )
            }
          />
        </Box>
        <FormControl fullWidth>
          <InputLabel id="agency-select-label">{t('updateClientAgency.inputLabel')}</InputLabel>
          <Select
            labelId="agency-select-label"
            value={String(selectedAgency)}
            size="small"
            onChange={handleAgencyChange}
            disabled={isDisabled}
          >
            {agencies?.items.map((agency) => (
              <MenuItem key={agency.id} value={agency.id}>
                {agency.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </CardContent>
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>{t('updateClientAgency.confirm')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('updateClientAgency.dialog1')}
            <ul>
              <li>{t('updateClientAgency.dialog2')}</li>
              <li>{t('updateClientAgency.dialog3')}</li>
            </ul>
            {t('updateClientAgency.dialog4')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} color="secondary">
            {t('common.cancel')}
          </Button>
          <Button onClick={handleConfirmChange} color="primary">
            {t('common.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};
